import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Building2, Check, ChevronDown, Copy, FileText, Inbox, LayoutDashboard, Link2, ListChecks, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import { Mark } from "@/components/public/Logo";
import { OrderStatus, QuoteStatus, InquiryStatus, Chip } from "@/components/drift/StatusBadge";
import { Markdown } from "@/components/drift/ai/Markdown";
import { CompanyMark } from "@/components/drift/CompanyMark";
import { fmtNOK, fmtTime, relDay, CUSTOMER_TYPE } from "@/lib/format";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   Messages. User turns sit right on a soft surface; assistant turns are
   plain text behind the mark, with the tool steps shown as a quiet
   work log above the answer. Data from tools renders as compact rows.
   ------------------------------------------------------------------ */

const DOC_KIND = { tilbod: "Tilbodstekst", epost: "E-post", rapport: "Rapport", sjekkliste: "Sjekkliste", referat: "Møtereferat", prosedyre: "Prosedyre", sosiale_medium: "Innlegg", notat: "Notat", anna: "Dokument" };
const STEP_ICON = { search_orders: Search, get_order: Search, search_customers: Building2, get_customer: Building2, list_inquiries: Inbox, list_quotes: Mail, dashboard_summary: LayoutDashboard, day_plan: ListChecks, write_document: FileText, update_document: FileText };

const rowCls = "flex min-w-0 items-center gap-3 px-3 py-2 text-[13px] transition-colors duration-150 hover:bg-accent/40 focus-visible:outline-none focus-visible:bg-accent/40";

export function DataCard({ data, onOpenCanvas }) {
  if (!data || !data.kind) return null;
  const { kind, items = [] } = data;
  if (kind === "canvas") {
    return (
      <button type="button" onClick={onOpenCanvas} className="group flex w-full items-center gap-3 rounded-xl bg-card p-3 text-left shadow-panel transition-[background-color] duration-150 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="ai-doc-card">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary"><FileText className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden /></span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-medium">{data.title}</span>
          <span className="block text-[12px] text-muted-foreground">{DOC_KIND[data.doc_kind] || "Dokument"} · versjon {data.version}</span>
        </span>
        <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-muted-foreground group-hover:text-foreground">Opne <ArrowUpRight className="link-diag h-3.5 w-3.5" strokeWidth={1.75} aria-hidden /></span>
      </button>
    );
  }
  if (items.length === 0) return null;
  if (kind === "kpis") {
    return (
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-border/70 shadow-panel sm:grid-cols-3 [&>*]:bg-card" data-testid="ai-kpi-card">
        {items.map((k) => (
          <Link key={k.label} to={k.link || "/drift"} className="block p-3 transition-colors hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <span className="block text-[12px] text-muted-foreground">{k.label}</span>
            <span className="t-num mt-1 block text-2xl leading-none">{k.value}</span>
          </Link>
        ))}
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl bg-card shadow-panel divide-y divide-border/60" data-testid={`ai-${kind}-card`}>
      {kind === "orders" && items.map((o) => (
        <Link key={o.id} to={o.link} className={rowCls}>
          <span className="hidden w-[86px] shrink-0 font-mono text-[12px] text-muted-foreground tabular sm:inline">{o.ref}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{o.title}</span>
            <span className="block truncate text-[12px] text-muted-foreground">{o.customer_name}{o.assigned_name ? ` · ${o.assigned_name}` : ""}</span>
          </span>
          <span className="hidden shrink-0 font-mono text-[12px] text-muted-foreground tabular sm:inline">{o.scheduled_date ? `${relDay(o.scheduled_date)}${o.scheduled_time ? ` ${o.scheduled_time}` : ""}` : ""}</span>
          <OrderStatus status={o.status} size="sm" />
        </Link>
      ))}
      {kind === "customers" && items.map((c) => (
        <Link key={c.id} to={c.link} className={rowCls}>
          <CompanyMark name={c.name} brand={c.brand} type={c.type} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{c.name}</span>
            <span className="block truncate text-[12px] text-muted-foreground">{c.address}</span>
          </span>
          <Chip>{CUSTOMER_TYPE[c.type] || c.type}</Chip>
        </Link>
      ))}
      {kind === "quotes" && items.map((q) => (
        <Link key={q.id} to={q.link} className={rowCls}>
          <span className="hidden w-[86px] shrink-0 font-mono text-[12px] text-muted-foreground tabular sm:inline">{q.ref}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{q.title}</span>
            <span className="block truncate text-[12px] text-muted-foreground">{q.customer_name}</span>
          </span>
          <span className="hidden shrink-0 text-[12.5px] tabular sm:inline">{q.amount != null ? fmtNOK(q.amount) : ""}</span>
          <QuoteStatus status={q.status} size="sm" />
        </Link>
      ))}
      {kind === "inquiries" && items.map((i) => (
        <Link key={i.id} to={i.link} className={rowCls}>
          <span className="hidden w-[104px] shrink-0 font-mono text-[12px] text-muted-foreground tabular sm:inline">{i.ref}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{i.company || i.name}</span>
            <span className="block truncate text-[12px] text-muted-foreground">{i.ai?.tittel || i.message}</span>
          </span>
          <InquiryStatus status={i.status} size="sm" />
        </Link>
      ))}
    </div>
  );
}

function Step({ step, open, onToggle, onOpenCanvas, live }) {
  const Icon = STEP_ICON[step.name] || Search;
  const pending = step.phase === "start";
  const hasData = step.data && (step.data.kind === "canvas" || step.data.kind === "kpis" || (step.data.items || []).length > 0);
  return (
    <div className="min-w-0 max-w-full" data-testid="ai-step" data-step={step.name} data-phase={step.phase || "done"}>
      <button
        type="button"
        onClick={hasData ? onToggle : undefined}
        className={cn("-mx-1.5 flex h-7 max-w-full items-center gap-2 rounded-md px-1.5 text-left text-[12.5px] text-muted-foreground transition-colors", hasData && "hover:bg-accent/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}
        aria-expanded={hasData ? open : undefined}
        title={step.summary ? `${step.label} · ${step.summary}` : step.label}
      >
        {pending ? <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center"><span className="live-dot h-1.5 w-1.5 rounded-full bg-brand" /></span> : <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden />}
        <span className={cn("shrink-0 whitespace-nowrap", pending && live && "animate-pulse")}>{step.label}</span>
        {step.summary && <span className="min-w-0 truncate text-foreground/80">· {step.summary}</span>}
        {hasData && <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform duration-200", open && "rotate-180")} strokeWidth={1.75} aria-hidden />}
      </button>
      {hasData && open && <div className="mb-2 mt-1 min-w-0"><DataCard data={step.data} onOpenCanvas={() => onOpenCanvas?.(step.data)} /></div>}
    </div>
  );
}

export function Steps({ steps, live = false, onOpenCanvas }) {
  const [openIds, setOpenIds] = useState(null);
  if (!steps || steps.length === 0) return null;
  // Open everything by default when the log is short; the canvas card is always visible.
  const dataSteps = steps.filter((s) => s.data);
  const isOpen = (s) => {
    if (openIds) return openIds.has(s.id);
    if (s.data?.kind === "canvas") return true;
    return dataSteps.length <= 2;
  };
  const toggle = (s) => {
    setOpenIds((prev) => {
      const next = new Set(prev || steps.filter(isOpen).map((x) => x.id));
      if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
      return next;
    });
  };
  return (
    <div className="mb-2.5 grid min-w-0 gap-0.5" data-testid="ai-steps">
      {steps.map((s) => <Step key={s.id} step={s} open={isOpen(s)} onToggle={() => toggle(s)} onOpenCanvas={onOpenCanvas} live={live} />)}
    </div>
  );
}

function CopyButton({ text }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard?.writeText(text).then(() => { setOk(true); toast.success("Kopiert"); setTimeout(() => setOk(false), 1500); }); }}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label="Kopier svaret"
      data-testid="ai-message-copy"
    >
      {ok ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />}
    </button>
  );
}

export function UserMessage({ m }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }} className="flex flex-col items-end gap-1.5" data-testid="ai-message-user">
      {m.context?.label && (
        <Link to={m.context.link || "#"} className="inline-flex h-6 items-center gap-1.5 rounded-md bg-secondary px-2 text-[11.5px] font-medium text-muted-foreground hover:text-foreground" data-testid="ai-message-context">
          <Link2 className="h-3 w-3" strokeWidth={1.75} aria-hidden /> {m.context.label}
        </Link>
      )}
      <div className="max-w-[86%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-secondary px-4 py-2.5 text-[14.5px] leading-[1.55] sm:max-w-[78%]">{m.content}</div>
    </motion.div>
  );
}

export function AssistantMessage({ m, live = false, onOpenCanvas }) {
  const thinking = live && !m.content && !(m.steps || []).some((s) => s.phase === "done");
  return (
    <div className="group/msg flex min-w-0 gap-3.5" data-testid={live ? "ai-message-live" : "ai-message-assistant"}>
      <Mark className="mt-0.5 h-6 w-6 shrink-0" title="" aria-hidden="true" />
      <div className="min-w-0 flex-1 overflow-hidden">
        <Steps steps={m.steps} live={live} onOpenCanvas={onOpenCanvas} />
        {thinking && (
          <div className="flex h-6 items-center gap-1" aria-label="Tenkjer" data-testid="ai-thinking">
            <span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" /><span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" /><span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" />
          </div>
        )}
        {m.error ? (
          <p className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[13.5px] tone-danger ring-1" role="alert" data-testid="ai-message-error">{m.error}</p>
        ) : (
          m.content && <Markdown text={m.content} className={cn(live && "stream-caret")} />
        )}
        {m.partial && !live && <p className="mt-1 text-[11.5px] text-muted-foreground">Stoppa før svaret var ferdig.</p>}
        {!live && m.content && (
          <div className="mt-1.5 flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover/msg:opacity-100 focus-within:opacity-100">
            <CopyButton text={m.content} />
            {m.canvas_version && onOpenCanvas && (
              <button type="button" onClick={() => onOpenCanvas({ version: m.canvas_version })} className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="ai-message-open-canvas">
                <FileText className="h-3.5 w-3.5" strokeWidth={1.75} /> Dokument v{m.canvas_version}
              </button>
            )}
            <span className="ml-1 font-mono text-[11px] text-muted-foreground/70">{fmtTime(m.created_at)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
